// =============================================================================
// Client-side API for "Projekte" — solo per-owner P&L ledgers.
//
// Schema:
//   projects(id, owner_id, name, icon, color, archived, created_at)
//   uvprinter_transactions(id, owner_id, project_id, txn_date, kind,
//                          amount, category, note, created_at)
//     kind ∈ {'expense', 'income'}
//
// Profit / break-even are derived from the per-project transaction list:
//   netProfit       = sum(income) − sum(expense)
//   isBreakEven     = netProfit ≥ 0
// =============================================================================
import { supabase } from './supabase';

// Generic quick-pick categories. The user can always type their own.
export const EXPENSE_CATEGORIES = [
  'Material',
  'Geräte',
  'Strom',
  'Wartung',
  'Versand',
  'Verpackung',
  'Werbung',
  'Software',
  'Sonstiges'
];

export const INCOME_CATEGORIES = ['Verkauf', 'Auftrag', 'Sonstiges'];

// -------- Projects CRUD ----------------------------------------------------

export async function listProjects() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, kind, icon, color, archived, created_at')
    .order('archived', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapProject);
}

export async function addProject({
  name,
  kind = 'profit',
  icon = null,
  color = null
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name fehlt');
  if (kind !== 'profit' && kind !== 'time') throw new Error('Art ungültig');
  const { data, error } = await supabase
    .from('projects')
    .insert({
      owner_id: user.id,
      name: trimmed,
      kind,
      icon: (icon || '').trim() || null,
      color: (color || '').trim() || null
    })
    .select('id, name, kind, icon, color, archived, created_at')
    .single();
  if (error) throw error;
  return mapProject(data);
}

export async function updateProject(id, patch) {
  const row = {};
  if (patch.name !== undefined) {
    const trimmed = (patch.name || '').trim();
    if (!trimmed) throw new Error('Name fehlt');
    row.name = trimmed;
  }
  if (patch.icon !== undefined) row.icon = (patch.icon || '').trim() || null;
  if (patch.color !== undefined) row.color = (patch.color || '').trim() || null;
  if (patch.archived !== undefined) row.archived = !!patch.archived;
  if (patch.kind !== undefined) {
    if (patch.kind !== 'profit' && patch.kind !== 'time') {
      throw new Error('Art ungültig');
    }
    row.kind = patch.kind;
  }
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase.from('projects').update(row).eq('id', id);
  if (error) throw error;
}

export async function deleteProject(id) {
  // Transactions cascade via FK on delete.
  const { error } = await supabase.from('projects').delete().eq('id', id);
  if (error) throw error;
}

function mapProject(r) {
  return {
    id: r.id,
    name: r.name,
    kind: r.kind || 'profit',
    icon: r.icon || '',
    color: r.color || '',
    archived: !!r.archived,
    createdAt: r.created_at
  };
}

// -------- Transactions CRUD ------------------------------------------------

export async function listTransactions(projectId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || !projectId) return [];
  const { data, error } = await supabase
    .from('uvprinter_transactions')
    .select('id, project_id, txn_date, kind, amount, category, note, created_at')
    .eq('project_id', projectId)
    .order('txn_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapTxn);
}

// Fetch transactions for every project at once (used to build the list-view
// summary card without N+1 round trips).
export async function listAllTransactions() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('uvprinter_transactions')
    .select('id, project_id, txn_date, kind, amount, category, note, created_at')
    .order('txn_date', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapTxn);
}

export async function addTransaction({
  projectId,
  txnDate,
  kind,
  amount,
  category = null,
  note = null
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  if (!projectId) throw new Error('Projekt fehlt');
  if (kind !== 'expense' && kind !== 'income') throw new Error('Art ungültig');
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Betrag ungültig');
  if (!txnDate) throw new Error('Datum fehlt');
  const { data, error } = await supabase
    .from('uvprinter_transactions')
    .insert({
      owner_id: user.id,
      project_id: projectId,
      txn_date: txnDate,
      kind,
      amount: amt,
      category: (category || '').trim() || null,
      note: (note || '').trim() || null
    })
    .select('id, project_id, txn_date, kind, amount, category, note, created_at')
    .single();
  if (error) throw error;
  return mapTxn(data);
}

export async function updateTransaction(id, patch) {
  const row = {};
  if (patch.txnDate !== undefined) row.txn_date = patch.txnDate;
  if (patch.kind !== undefined) {
    if (patch.kind !== 'expense' && patch.kind !== 'income') {
      throw new Error('Art ungültig');
    }
    row.kind = patch.kind;
  }
  if (patch.amount !== undefined) {
    const amt = Number(patch.amount);
    if (!Number.isFinite(amt) || amt <= 0) throw new Error('Betrag ungültig');
    row.amount = amt;
  }
  if (patch.category !== undefined) row.category = (patch.category || '').trim() || null;
  if (patch.note !== undefined) row.note = (patch.note || '').trim() || null;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('uvprinter_transactions')
    .update(row)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteTransaction(id) {
  const { error } = await supabase
    .from('uvprinter_transactions')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

function mapTxn(r) {
  return {
    id: r.id,
    projectId: r.project_id,
    txnDate: r.txn_date,
    kind: r.kind,
    amount: Number(r.amount),
    category: r.category || '',
    note: r.note || '',
    createdAt: r.created_at
  };
}

// -------- Aggregations -----------------------------------------------------

export function summarise(transactions) {
  let expense = 0;
  let income = 0;
  for (const t of transactions) {
    if (t.kind === 'expense') expense += t.amount;
    else if (t.kind === 'income') income += t.amount;
  }
  const net = income - expense;
  return {
    totalExpense: expense,
    totalIncome: income,
    netProfit: net,
    isProfitable: net > 0,
    isBreakEven: net >= 0,
    count: transactions.length
  };
}

export function byCategory(transactions, kind) {
  const m = new Map();
  for (const t of transactions) {
    if (t.kind !== kind) continue;
    const cat = t.category || 'Sonstiges';
    m.set(cat, (m.get(cat) || 0) + t.amount);
  }
  return [...m.entries()]
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);
}

export function cumulativeSeries(transactions) {
  if (!transactions.length) return [];
  const sorted = [...transactions].sort((a, b) =>
    String(a.txnDate).localeCompare(String(b.txnDate))
  );
  const byDate = new Map();
  for (const t of sorted) {
    const delta = t.kind === 'income' ? t.amount : -t.amount;
    byDate.set(t.txnDate, (byDate.get(t.txnDate) || 0) + delta);
  }
  const out = [];
  let running = 0;
  for (const [date, delta] of byDate) {
    running += delta;
    out.push({ date, balance: Math.round(running * 100) / 100 });
  }
  return out;
}

export function breakEvenDate(transactions) {
  const series = cumulativeSeries(transactions);
  let everNegative = false;
  for (const point of series) {
    if (point.balance < 0) {
      everNegative = true;
      continue;
    }
    if (everNegative && point.balance >= 0) return point.date;
  }
  return null;
}
